import React, { useRef, useEffect, useState } from 'react';
import { FaClock } from 'react-icons/fa';
import { Post, Comment, UserData } from './types';
import { PostHeader } from './PostHeader';
import { PostContent } from './PostContent';
import { PostMedia } from './PostMedia';
import { PostActions } from './PostActions';
import { PostMenu } from './PostMenu';
import { PostComments } from './PostComments';

interface PostCardProps {
  darkMode: boolean;
  post: Post;
  user: any;
  isLiked: boolean;
  isSaved: boolean;
  isFollowing: boolean;
  followLoading: boolean;
  showComments: boolean;
  showPostMenu: boolean;
  comments: Comment[];
  commentText: string;
  loadingComments: boolean;
  userData: UserData;
  onLike: () => void;
  onToggleComments: () => void;
  onShare: () => void;
  onToggleSave: () => void;
  onFollow: () => void;
  onDelete: () => void;
  onToggleMenu: () => void;
  onReport: () => void;
  onCommentChange: (text: string) => void;
  onSubmitComment: () => void;
  formatTime: (isoString: string) => string;
  formatCount: (count: number) => string;
}

export const PostCard: React.FC<PostCardProps> = ({
  darkMode, post, user, isLiked, isSaved, isFollowing,
  followLoading, showComments, showPostMenu, comments,
  commentText, loadingComments, userData,
  onLike, onToggleComments, onShare, onToggleSave, onFollow,
  onDelete, onToggleMenu, onReport, onCommentChange, onSubmitComment,
  formatTime, formatCount,
}) => {
  const isOptimistic = post.id.startsWith('optimistic_');
  const isOwnPost = user && String(post.user_id).trim() === String(user.id).trim();

  const cardRef = useRef<HTMLElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;

    // Trigger at 55% visibility — post is well in view
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsFocused(entry.intersectionRatio >= 0.55);
      },
      { threshold: [0, 0.3, 0.55, 0.8, 1.0] }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <style id="onyx-postcard-css">{`
        .onyx-post-card {
          overflow: hidden;
          will-change: transform, opacity;
          /* Smooth scale + opacity transition */
          transition:
            transform 0.35s cubic-bezier(0.34, 1.2, 0.64, 1),
            opacity 0.35s ease,
            border-color 0.2s ease;
          /* Center the card in whatever feed container wraps it */
          margin-left: auto;
          margin-right: auto;
          width: 100%;
          /* Cap width so it never fills a large viewport */
          max-width: clamp(340px, 92vw, 620px);
        }

        /* Focused card: scale up slightly */
        .onyx-post-card.onyx-focused {
          transform: scale(1.025);
          opacity: 1;
        }

        /* Unfocused card: scale down slightly and dim */
        .onyx-post-card.onyx-unfocused {
          transform: scale(0.97);
          opacity: 0.72;
        }

        /* Default (not yet observed or ambiguous): neutral */
        .onyx-post-card.onyx-neutral {
          transform: scale(1);
          opacity: 1;
        }

        .onyx-post-media {
          padding: 0 12px 12px;
        }

        @media (min-width: 640px) {
          .onyx-post-media {
            padding: 0 16px 12px;
          }
          .onyx-media-image {
            max-width: 65%;
          }
          .onyx-media-image img {
            max-height: 360px;
          }
        }

        @media (hover: hover) {
          .onyx-media-image:hover .onyx-media-expand-hint {
            opacity: 1 !important;
          }
        }

        @media (max-width: 375px) {
          .onyx-post-header { padding: 10px 12px 8px; }
          .onyx-post-actions { padding: 8px 12px 10px; }
          .onyx-post-content { padding: 0 12px 10px; }
          .onyx-post-media { padding: 0 12px 10px; }
        }

        .onyx-post-card:last-child {
          margin-bottom: env(safe-area-inset-bottom, 0px);
        }

        @media (hover: none) {
          .onyx-post-card:active {
            opacity: 0.97;
          }
        }

        /* Respect reduced-motion preferences */
        @media (prefers-reduced-motion: reduce) {
          .onyx-post-card {
            transition: opacity 0.2s ease !important;
          }
          .onyx-post-card.onyx-focused,
          .onyx-post-card.onyx-unfocused,
          .onyx-post-card.onyx-neutral {
            transform: none !important;
          }
        }
      `}</style>

      <article
        ref={cardRef}
        className={`onyx-post-card ${isFocused ? 'onyx-focused' : 'onyx-unfocused'}`}
        style={{
          background: darkMode ? '#0f2424' : '#ffffff',
          border: `1.5px solid ${
            isFocused
              ? darkMode ? 'rgba(90,184,184,0.28)' : 'rgba(13,26,26,0.18)'
              : darkMode ? 'rgba(90,184,184,0.10)' : 'rgba(13,26,26,0.07)'
          }`,
          borderRadius: '14px',
          opacity: isOptimistic ? 0.72 : undefined,
        }}
      >
        {/* Optimistic indicator */}
        {isOptimistic && (
          <div style={{ padding: '8px 14px 4px' }}>
            <div
              className="flex items-center gap-2"
              style={{ color: '#5ab8b8', fontSize: '10px', fontWeight: 900, letterSpacing: '1px' }}
            >
              <FaClock style={{ animation: 'pulse 1.5s ease-in-out infinite' }} />
              <span>POSTING...</span>
            </div>
          </div>
        )}

        {/* Header */}
        <div
          className="onyx-post-header"
          style={{ padding: '12px 14px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <PostHeader
            darkMode={darkMode}
            author={post.author}
            createdAt={post.created_at}
            isOwnPost={isOwnPost}
            isFollowing={isFollowing}
            followLoading={followLoading}
            onFollow={onFollow}
            formatTime={formatTime}
          />
          <PostMenu
            darkMode={darkMode}
            postId={post.id}
            isOwnPost={isOwnPost}
            isOptimistic={isOptimistic}
            isOpen={showPostMenu}
            onToggle={onToggleMenu}
            onShare={onShare}
            onDelete={onDelete}
            onReport={onReport}
          />
        </div>

        {/* Content */}
        <div className="onyx-post-content" style={{ padding: '0 14px 10px' }}>
          <PostContent darkMode={darkMode} content={post.content} />
        </div>

        {/* Media */}
        <PostMedia
          darkMode={darkMode}
          mediaUrl={post.media_url}
          mediaType={post.media_type}
        />

        {/* Actions */}
        <div className="onyx-post-actions" style={{ padding: '4px 14px 12px' }}>
          <PostActions
            darkMode={darkMode}
            postId={post.id}
            likes={post.likes}
            commentsCount={post.comments_count}
            shares={post.shares}
            isLiked={isLiked}
            isSaved={isSaved}
            isOptimistic={isOptimistic}
            showComments={showComments}
            onLike={onLike}
            onToggleComments={onToggleComments}
            onShare={onShare}
            onToggleSave={onToggleSave}
            formatCount={formatCount}
          />
        </div>

        {/* Comments */}
        {showComments && (
          <PostComments
            darkMode={darkMode}
            postId={post.id}
            comments={comments}
            commentText={commentText}
            loading={loadingComments}
            userData={userData}
            onCommentChange={onCommentChange}
            onSubmitComment={onSubmitComment}
            formatTime={formatTime}
          />
        )}
      </article>
    </>
  );
};